import React, { FC } from "react";
import { Fade } from "react-awesome-reveal";
import { GatsbyImage } from "gatsby-plugin-image";
import { getGatsbyImage } from "gatsby-plugin-storyblok-image";
import classNames from "classnames";
import loadable from "@loadable/component";
import {
  BlockHeader,
  HeaderLevel,
} from "@app/components/common/block-header/BlockHeader";
import { BlockBody } from "@app/components/common/block-body/BlockBody";
import {
  Asset,
  Button,
  MediaOptions,
  RichText,
} from "@app/components/base-page/models";
import { ContentAlign } from "@app/components/base-page/enums";
import { FileType, checkFileType } from "@app/helpers/check-file-type";
import { useGenericData } from "@app/hooks/use-generic-data/useGenericData";
import { DEFAULT_FADE_DURATION } from "@app/constants";
import { LinkButtonAdapter } from "../../../link-button-adapter/LinkButtonAdapter";

const DEFAULT_BG_POSITION = 50;
const DEFAULT_OVERLAY = 30;
const ReactPlayer = loadable(() => import("react-player"));

type Props = {
  title: RichText;
  subtitle: RichText;
  contentAlign: ContentAlign;
  textAlign?: ContentAlign;
  button?: Button[];
  media: Asset;
  mediaOptions: MediaOptions[];
};

export const HeroStatic: FC<Props> = ({
  title,
  subtitle,
  contentAlign,
  textAlign,
  media,
  mediaOptions,
  button,
}) => {
  const genericData = useGenericData();

  return (
    <Fade duration={DEFAULT_FADE_DURATION} triggerOnce>
      <div
        className={classNames(
          "relative flex flex-col gap-y-10 px-5 pt-15",
          "lg:px-16 lg:pt-25",
          "xl:min-w-screen xl:-mt-20 xl:pb-[152px] xl:pt-[152px]",
          "xl:gap-y-0 xl:px-24",
          "xl:items-center xl:justify-center",
          {
            "xl:min-h-[calc(100vh-36px)]":
              genericData?.notificationBar?.isEnabled,
            "xl:min-h-screen": !genericData?.notificationBar?.isEnabled,
          },
        )}
      >
        <div className="xl:absolute xl:bottom-0 xl:left-0 xl:right-0 xl:top-0">
          {checkFileType(media.filename) === FileType.VIDEO ? (
            <ReactPlayer
              className={classNames(
                "aspect-square [&>video]:rounded-sm [&>video]:object-cover",
                "md:aspect-[3/2]",
                "xl:aspect-auto [&>video]:xl:rounded-none",
              )}
              url={media.filename}
              width="100%"
              height="100%"
              playing
              muted
              loop
              playsinline
              config={{
                file: {
                  attributes: { disablePictureInPicture: true },
                },
              }}
            />
          ) : (
            <GatsbyImage
              image={getGatsbyImage(media.filename, {
                layout: "fullWidth",
              })}
              alt={media.alt}
              className={classNames(
                "aspect-square rounded-sm",
                "md:aspect-[3/2]",
                "xl:aspect-auto xl:h-full xl:w-full xl:rounded-none",
              )}
              imgStyle={{
                objectPosition: `${
                  mediaOptions[0]?.xAxis ?? DEFAULT_BG_POSITION
                }% ${mediaOptions[0]?.yAxis ?? DEFAULT_BG_POSITION}%`,
              }}
            />
          )}
        </div>
        <div
          className={classNames(
            "bottom-0 left-0 right-0 top-0 hidden",
            "xl:absolute xl:block",
          )}
          style={{
            backgroundColor: `rgba(0, 0, 0, ${Number(mediaOptions[0]?.overlay ?? DEFAULT_OVERLAY) / 100})`,
          }}
        ></div>

        <div
          className={classNames(
            "relative order-first w-full",
            "text-primary-content",
            "md:max-w-[75%]",
            "xl:max-w-container-wide xl:text-center",
            "xl:order-last xl:flex",
            {
              "xl:justify-end": contentAlign === ContentAlign.RIGHT,
              "xl:justify-center": contentAlign === ContentAlign.CENTER,
            },
          )}
        >
          <div
            className={classNames("xl:w-1/2", {
              "xl:text-left": textAlign === ContentAlign.LEFT,
              "xl:text-right": textAlign === ContentAlign.RIGHT,
            })}
          >
            <div
              className={classNames(
                "mb-5 text-large",
                "lg:text-xx-large",
                "xl:text-huge",
              )}
            >
              <BlockHeader richText={title} headerLevel={HeaderLevel.H1} />
            </div>
            <div
              className={classNames(
                "text-regular text-base-400",
                "xl:text-primary-content",
                {
                  "mb-8": button?.length,
                },
              )}
            >
              <BlockBody richText={subtitle} />
            </div>
            {button?.length ? <LinkButtonAdapter data={button[0]} /> : null}
          </div>
        </div>
      </div>
    </Fade>
  );
};
